import type { ZuoraLocales } from '@platform-ui/design-system/dist/types/Theme';
import { NavigationVariant } from './hooks/useNavigationVariant';
import { UserProfileConfig } from './GlobalNav/utils/profile-configs';

export const isChildMenuItem = (item: unknown): item is ChildMenuItem => {
  return (
    (item as ChildMenuItem).url !== undefined &&
    (item as ChildParentMenuItem).children === undefined
  );
};

export const isChildParentMenuItem = (
  item: unknown,
): item is ChildParentMenuItem => {
  return (item as ChildParentMenuItem).children !== undefined;
};

export interface ChildMenuItem
  extends Pick<ParentMenuItem, 'id' | 'label' | 'onClick' | 'chip'> {
  url?: string;
  parentId: string;
  parentLabel: string;
}

export interface ChildParentMenuItem
  extends Pick<ParentMenuItem, 'id' | 'label' | 'onClick' | 'chip'> {
  url?: string;
  parentId: string;
  parentLabel: string;
  children: ChildMenuItem[];
}

export type ParentMenuItemChild = ChildMenuItem | ChildParentMenuItem;

export interface ParentMenuItem {
  id: string;
  label: string;
  url?: string | null;
  onClick?: () => void;
  parentId: null;
  parentLabel: null;
  children: ParentMenuItemChild[];
  chip?: string;
}

// GET /platform/api/menus
export interface Menus {
  menus: ParentMenuItem[];
  settings: ParentMenuItem[];
  userName: string;
  coreUserId: string;
  tenantName: string;
  envBanner: {
    display: 'ON' | 'OFF';
    backgroundColor: string;
    textColor: string;
    bannerLabel: string;
  };
  navigationState: {
    [key: string]: boolean;
    navigationShow: boolean;
    navigationDock: boolean;
    adminExpand: boolean;
    billingExpand: boolean;
    customersExpand: boolean;
    financeExpand: boolean;
    insightsExpand: boolean;
    productExpand: boolean;
    paymentsExpand: boolean;
    reportingExpand: boolean;
    workflowExpand: boolean;
    myConnectExpand: boolean;
    collectionsExpand: boolean;
    commerceExpand: boolean;
    analyticsExpand: boolean;
    customerAdminExpand: boolean;
    systemHealthExpand: boolean;
  };
  entities: Entity[] | null;
  success: boolean;
  banner: {
    display: boolean;
    backgroundColor: string;
    textColor: string;
    bannerLabel: string;
    tenantName: string;
  };
  ZuoraCurrentEntity?: string;
}

export enum PageType {
  list,
  edit,
  detail,
  create,
}

export type NavItem = ParentMenuItem | ChildMenuItem;

/**
 * Canonical (BCP-47, hyphenated) language codes the nav supports. Region-
 * qualified codes use a hyphen (e.g. `zh-CN`); underscore variants coming from
 * Billing (`zh_CN`) are normalized via `normalizeLanguageCode` in `i18n.tsx`.
 * English is bundled; every other language is loaded lazily from
 * `src/nav/locales/<code>.ts`.
 */
export type ZuoraLanguages =
  | 'bg' // Bulgarian
  | 'bn' // Bengali
  | 'ca' // Catalan
  | 'cs' // Czech
  | 'da' // Danish
  | 'de' // German
  | 'el' // Greek
  | 'en' // English
  | 'es' // Spanish
  | 'et' // Estonian
  | 'fi' // Finnish
  | 'fil' // Filipino
  | 'fr' // French
  | 'hi' // Hindi
  | 'hr' // Croatian
  | 'hu' // Hungarian
  | 'id' // Indonesian
  | 'it' // Italian
  | 'ja' // Japanese
  | 'kk' // Kazakh
  | 'km' // Khmer
  | 'ko' // Korean
  | 'lt' // Lithuanian
  | 'lv' // Latvian
  | 'ms' // Malay
  | 'mt' // Maltese
  | 'my' // Burmese
  | 'nb' // Norwegian Bokmål
  | 'nl' // Dutch
  | 'pl' // Polish
  | 'pt' // Portuguese
  | 'ro' // Romanian
  | 'ru' // Russian
  | 'sk' // Slovak
  | 'sl' // Slovenian
  | 'sv' // Swedish
  | 'ta' // Tamil
  | 'th' // Thai
  | 'tr' // Turkish
  | 'uk' // Ukrainian
  | 'vi' // Vietnamese
  | 'zh-CN' // Simplified Chinese
  | 'zh-TW'; // Traditional Chinese

export interface AccessibleEntity {
  id: string;
  entityId: string;
  name: string;
  parentEntityId?: string;
  globalEntityId: string;
  parentNodeId?: string;
  globalNodeId: string;
  identifier: string;
  status: string;
  authEntityId: string;
  globalNode: boolean;
  multiEntity: boolean;
  provisioned: boolean;
}

// shape of "/platform/api/identity" response, plus additional properties
export interface Identity {
  coreUserId: string;
  username: string;
  workEmail: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  locale: ZuoraLocales;
  language: ZuoraLanguages;
  twoFaEnabled: boolean;
  twoFaSetup: boolean;
  ssoEnabled: boolean;
  permissions: string[];
  defaultEntityId: string;
  defaultTenantId: string;
  tenantId: string;
  accessibleEntities?: AccessibleEntity[];
  tenantName: string;
  tenantTimeZone: string;
  platformRole: string;
  insightsRole: string;
  globalTenantStatus: string;
  globalTenantTrialEndDate?: string;
  jobTitle: string;
  jobFunctions: string[];
  // additional properties
  entityId: string;
  userId: string;
  tenantMetadata?: TenantMetadata['info'];
  userRoleMap: Record<string, string>;
  userProfileId: string;
}

// shape of "/platform/gateway-proxy-v2/genesis/user/info" response
export interface GenesisUserInfoResponse {
  coreUserId: string;
  username: string;
  workEmail: string;
  firstName: string;
  lastName: string;
  status: string;
  locale: ZuoraLocales;
  language: ZuoraLanguages;
  twoFaEnabled: boolean;
  twoFaSetup: boolean;
  ssoEnabled: boolean;
  permissions: string[];
  defaultEntityId: string;
  defaultTenantId: string;
  tenantId: string;
  accessibleEntities?: [
    {
      id: string;
      entityId: string;
      name: string;
      parentEntityId?: string;
      globalEntityId: string;
      parentNodeId?: string;
      globalNodeId: string;
      identifier: string;
      status: string;
      authEntityId: string;
      globalNode: boolean;
      multiEntity: boolean;
      provisioned: boolean;
    },
  ];
  tenantName: string;
  tenantTimeZone: string;
}

// shape of "/apps/v1/identity" response
export interface AppsV1IdentityResponse {
  tenantId: string;
  tenantName: string;
  entityId: string;
  tenantStatus: string;
  tenantTrialEndDate?: string;
  timeZone: string;
  userId: string;
  userProfileId: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  locale: ZuoraLocales;
  language: ZuoraLanguages;
  jobTitle: string;
  jobFunctions: string[];
  insightsRole: string;
  platformRole: string;
  dashboardRole: string;
  dashboardLevel: string;
  lastModifiedTime: number;
  userRoleMap: Record<string, string>;
  success: boolean;
}

export interface Entity {
  id: string;
  name: string;
  displayName: string;
  locale: string;
  timezone: string;
  status: string;
  parentId: string | null;
  tenantId: string;
  accessible: boolean;
  targetUrl: string;
  owner: boolean;
}

export type EndChipType = 'default' | 'success' | 'warning';

export interface AppsV1NavigationMenuItem {
  id: string;
  label: string;
  url?: string | null;
  parentId?: string | null;
  parentLabel?: string | null;
  onClick?: () => void;
  chip?: string;
  endChip?: string;
  endChipType?: EndChipType;
}

export interface AppsV1NavigationSuccessResponse {
  menus: AppsV1NavigationMenuItem[];
  settings: [
    {
      id: string;
      label: string;
      url?: string;
      parentId?: string;
      parentLabel?: string;
    },
  ];
  userName: string;
  tenantName: string;
  envBanner: {
    display: string;
    backgroundColor: string;
    textColor: string;
    bannerLabel: string;
  };
  navigationState: {
    navigationShow: boolean;
    navigationDock: boolean;
    adminExpand: boolean;
    billingExpand: boolean;
    customersExpand: boolean;
    financeExpand: boolean;
    productExpand: boolean;
    insightsExpand: boolean;
    paymentsExpand: boolean;
    reportingExpand: boolean;
    workflowExpand: boolean;
    myConnectExpand: boolean;
    collectionsExpand: boolean;
    commerceExpand: boolean;
    analyticsExpand: boolean;
    customerAdminExpand: boolean;
    systemHealthExpand: boolean;
  };
  entities: Entity[] | null;
  success: true;
}

interface AppsV1NavigationFailureResponse {
  success: false;
  processId: string;
  reasons: { code: number; message: string }[];
  requestId: string;
}

// shape of "/apps/v1/navigation" response
export type AppsV1NavigationResponse =
  | AppsV1NavigationSuccessResponse
  | AppsV1NavigationFailureResponse;

// shape of "/apps/v1/navigation/universal-identity" response
export interface AppsV1NavigationUniveralIdentityResponse
  extends Omit<AppsV1NavigationSuccessResponse, 'entities'> {
  // Only present for tenants opted into "universal identity".
  entities: Entity[][] | null;
}

// shape of "/tenant-registry/tenantsServiceInfo/${data.tenantId}?name=USER_PILOT&type=USER_PILOT" response
export type TenantMetadata = {
  zuoraTenantId?: string;
  name?: string;
  type?: string;
  className?: string;
  info?: Record<string, string>;
  thubTenantId?: string;
};

// Types for Custom Objects
interface DefaultFields {
  Id: string;
  CreatedById: string;
  CreatedDate: string;
  UpdatedById: string;
  UpdatedDate: string;
}

interface CustomObjectProperty {
  enum?: string[];
  format?: 'date' | 'date-time' | 'uuid' | 'url';
  label: string;
  maximum?: number;
  minimum?: number;
  exclusiveMaximum?: boolean;
  exclusiveMinimum?: boolean;
  maxLength?: number;
  minLength?: number;
  origin: 'custom' | 'system';
  type: 'integer' | 'number' | 'string' | 'boolean';
  description?: string;
  default?: string | boolean | number;
  displayName?: boolean;
  multiselect?: boolean;
}

interface CustomObjectRelationship {
  cardinality: 'manyToOne' | 'oneToMany';
  fields: { [property: string]: string };
  namespace: string;
  object: string;
}

interface CustomObjectSchema {
  object: string;
  origin?: 'default' | 'system';
  type: string;
  label: string;
  title?: string;
  description?: string;
  required?: string[];
  filterable?: string[];
  unique?: string[];
  auditable?: string[];
  properties: { [key: string]: CustomObjectProperty };
  relationships?: CustomObjectRelationship[];
  enableRecordMigration?: boolean;
  enableCreateRecordAuditing?: boolean;
  enableDeleteRecordAuditing?: boolean;
}

interface CustomObjectDefinitionResponse extends DefaultFields {
  type: string;
  schema: CustomObjectSchema;
}

export type NavigationIcon =
  | 'feedback'
  | 'analytics'
  | 'apps'
  | 'architecture'
  | 'b2b'
  | 'billing'
  | 'book'
  | 'bookmark'
  | 'card'
  | 'caretDown'
  | 'caretLeft'
  | 'caretRight'
  | 'caretUp'
  | 'close'
  | 'code'
  | 'cog'
  | 'collect'
  | 'commandCenter'
  | 'copilot'
  | 'custom-avatar'
  | 'customObjects'
  | 'customers'
  | 'dashboard'
  | 'delivery'
  | 'edit'
  | 'expand'
  | 'fileUpload'
  | 'finance'
  | 'help'
  | 'hideMenu'
  | 'home'
  | 'identity'
  | 'inbox'
  | 'insights'
  | 'label'
  | 'list'
  | 'marketplace'
  | 'metering'
  | 'more'
  | 'newWindow'
  | 'oneid'
  | 'page'
  | 'payments'
  | 'person'
  | 'pin'
  | 'platform'
  | 'policies'
  | 'processAi'
  | 'reporting'
  | 'revenue'
  | 'settings'
  | 'setups'
  | 'ssp'
  | 'storage'
  | 'subscriber'
  | 'togai'
  | 'verticalMore'
  | 'workbench'
  | 'zephr'
  | 'zuoraAi'
  | 'paymentOverview'
  | 'paymentConnections'
  | 'paymentRouting'
  | 'paymentCheckout'
  | 'paymentTransactions'
  | 'paymentLinks'
  | 'paymentRuns'
  | 'paymentSchedules'
  | 'paymentRetry'
  | 'paymentMethods'
  | 'gatewayReconciliation'
  | 'globalPaymentsDashboard'
  | 'paymentSettings'
  | 'zuoraPayments'
  | 'refunds'
  | 'paymentMethodUpdater';

// Environment types from schema enum
export type EnvironmentType = 'prod' | 'sbx' | 'stg';

export interface NavigationItem {
  label: string;
  id: string;
  lock?: boolean;
  icon?: NavigationIcon;
  noIcon?: boolean;
  highlightIcon?: boolean;
  appName?: string;
  href?: string;
  menuPath?: string[];
  originalHref?: string;
  fullUrlMatch?: boolean;
  chip?: string;
  endChip?: string;
  endChipType?: EndChipType;
  expandable?: boolean;
  pinnable?: boolean;
  divider?: boolean;
  newWindow?: boolean;
  customEvent?: string;
  onItemClick?: (event: Event, href?: string) => void;
  popover?: {
    label: string;
    id: string;
    items: NavigationItem[];
    additionalItems?: Array<{
      label: string;
      id: string;
      items: NavigationItem[];
    }>;
  };
}

export interface UserInfo {
  firstName: string;
  lastName: string;
  userName: string;
  role: string;
  email: string;
  profileConfig?: UserProfileConfig;
}

export interface TenantInfo {
  tenantId: string;
  name: string;
  environment: string;
  isDefault: boolean;
  isCurrent: boolean;
  href: string;
  children?: TenantInfo[];
}

export interface Inbox {
  label?: string;
  url?: string;
  visible: boolean;
}

export interface AppSection {
  name: string;
  visible?: boolean;
  top?: {
    tenants?: TenantInfo[];
    search?: boolean;
    multiOrgMenu?: boolean;
    multiEntityMenu?: boolean;
    accountingDate?: string;
    inbox?: Inbox;
    approvalsCenter?: Inbox;
  };
  bottom?: NavigationItem[];
  settings?: {
    name: string;
    items: NavigationItem[];
  }[];
}

export interface NavigationData {
  version: string;
  primary: {
    top: NavigationItem[];
    bottom: NavigationItem[];
    user: UserInfo;
  };
  secondary?: {
    apps?: AppSection[];
  };
}

// shape for Custom Object Elba Response
export interface CustomObjectDefinitionsResponse {
  definitions: { [key: string]: CustomObjectDefinitionResponse };
  count: number;
}
export type RevenueIdentifyingProps = {
  revenueTenantId: string | null;
  revenueUserRole: string | null;
  revenueFirstName: string | null;
  revenueLastName: string | null;
  revenueUserStatus: string | null;
  revenueEmail: string | null;
  revenueUserName: string | null;
  revenueTenantName: string | null;
  revenueUserID: string | null;
  revenueUserLanguage: string | null;
};

export interface NavigationConfig {
  variant: NavigationVariant | null;
  appName?: string;
  enabledSlots?: string[];
  menuItems?: ParentMenuItem[];
  homePageUrl?: string;
}
